import { IS_VITEST } from '@/utils/runtimeEnv';

const DEFAULT_PROBE_TIMEOUT_MS = 2500;
const DEFAULT_REQUEST_TIMEOUT_MS = 120000;
export const CDP_SERVICE_PREFIX = '/feifan-xxl-art-py';
export const CDP_API_PREFIX = `${CDP_SERVICE_PREFIX}/api`;
export const CDP_ENDPOINTS = Object.freeze({
  health: `${CDP_API_PREFIX}/health`,
  importPdfLayers: `${CDP_API_PREFIX}/import-pdf-layers`,
  importTemplatePdf: `${CDP_API_PREFIX}/import-template-pdf`,
  importImageLayers: `${CDP_API_PREFIX}/import-image-layers`,
  importPsdLayers: `${CDP_API_PREFIX}/import-psd-layers`,
  importCdrLayers: `${CDP_API_PREFIX}/import-cdr-layers`,
  renderPdf: `${CDP_API_PREFIX}/render-pdf`,
  exportDesignPdf: `${CDP_API_PREFIX}/export-pdf`,
  templatePdfCache: `${CDP_API_PREFIX}/template-pdf-cache`,
  pdfParseTasks: `${CDP_API_PREFIX}/pdf-parse-tasks`,
  pdfPageCount: `${CDP_API_PREFIX}/pdf-page-count`,
});

const FALLBACK_ENGINE_BASES = [
  'https://xxl-gateway-daily.1605ai.com/feifan-xxl-art-py',
];

let resolvedBase = undefined;
let resolvingPromise = null;

function normalizeBase(base) {
  if (base == null) return null;
  const value = String(base).trim();
  if (!value || value === '/') return '';
  return value.replace(/\/+$/, '');
}

function splitBases(value) {
  return String(value || '')
    .split(/[\s,]+/)
    .map(normalizeBase)
    .filter(base => base != null && base !== '');
}

function isBrowserHttps() {
  return typeof window !== 'undefined' && window.location?.protocol === 'https:';
}

function isLocalBrowser() {
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname || '';
  return !host || host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

function isBlockedByMixedContent(base) {
  return isBrowserHttps() && /^http:\/\//i.test(base || '');
}

function addCandidate(list, base) {
  const normalized = normalizeBase(base);
  if (normalized == null || isBlockedByMixedContent(normalized)) return;
  if (!list.includes(normalized)) list.push(normalized);
}

function joinBasePath(base, path) {
  let normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`;
  const normalizedBase = normalizeBase(base) || '';
  if (normalizedBase.endsWith(CDP_SERVICE_PREFIX) && normalizedPath.startsWith(`${CDP_SERVICE_PREFIX}/`)) {
    normalizedPath = normalizedPath.slice(CDP_SERVICE_PREFIX.length);
  }
  return normalizedBase ? `${normalizedBase}${normalizedPath}` : normalizedPath;
}

function cdpAssetPath(path) {
  const normalized = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`;
  if (normalized === CDP_SERVICE_PREFIX || normalized.startsWith(`${CDP_SERVICE_PREFIX}/`)) return normalized;
  return `${CDP_SERVICE_PREFIX}${normalized}`;
}

export function getCdpEngineCandidates(extraBases = []) {
  const candidates = [];
  const configured = [
    ...splitBases(import.meta.env.VITE_CDP_ENGINE_URL),
    ...splitBases(import.meta.env.VITE_PDF_SERVICE_URL),
  ];
  configured.forEach(base => addCandidate(candidates, base));

  if (typeof window !== 'undefined') {
    splitBases(window.__CDP_ENGINE_BASE__).forEach(base => addCandidate(candidates, base));
    try {
      splitBases(window.localStorage?.getItem('cdp_engine_base')).forEach(base => addCandidate(candidates, base));
    } catch { /* localStorage may be disabled */ }
  }

  splitBases(extraBases).forEach(base => addCandidate(candidates, base));
  if (isLocalBrowser() || import.meta.env.DEV) addCandidate(candidates, 'http://127.0.0.1:5174');

  FALLBACK_ENGINE_BASES.forEach(base => addCandidate(candidates, base));

  const hasLocation = typeof window !== 'undefined' && window.location;
  if (hasLocation && window.location.protocol !== 'file:') addCandidate(candidates, '');
  return candidates;
}

export function cdpEngineUrl(path, base = resolvedBase || '') {
  return joinBasePath(base, path);
}

export function cdpEngineAssetUrl(path, options = {}) {
  const value = String(path || '').trim();
  if (!value || /^(https?:|data:|blob:|file:)/i.test(value)) return value;
  const cleanPath = value.replace(/^\/+/, '');
  const prefixedPath = cdpAssetPath(cleanPath);
  const normalizedBase = normalizeBase(options.base ?? resolvedBase);
  if (normalizedBase) return joinBasePath(normalizedBase, prefixedPath);
  if (normalizedBase === '') return prefixedPath;

  const configuredBase = normalizeBase(import.meta.env.VITE_CDP_ENGINE_URL)
    || normalizeBase(import.meta.env.VITE_PDF_SERVICE_URL);
  if (configuredBase && !isBlockedByMixedContent(configuredBase)) {
    return joinBasePath(configuredBase, prefixedPath);
  }
  if (isLocalBrowser() || import.meta.env.DEV) return joinBasePath('http://127.0.0.1:5174', prefixedPath);
  return prefixedPath;
}

function createTimeoutSignal(timeoutMs, externalSignal) {
  const controller = new AbortController();
  let timer = null;

  const abort = () => {
    if (!controller.signal.aborted) controller.abort();
  };

  if (externalSignal?.aborted) {
    abort();
  } else if (externalSignal?.addEventListener) {
    externalSignal.addEventListener('abort', abort, { once: true });
  }

  if (Number(timeoutMs) > 0) {
    timer = window.setTimeout(abort, Number(timeoutMs));
  }

  return {
    signal: controller.signal,
    clear() {
      if (timer) window.clearTimeout(timer);
      if (externalSignal?.removeEventListener) externalSignal.removeEventListener('abort', abort);
    },
  };
}

async function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS) {
  const timeout = createTimeoutSignal(timeoutMs, options.signal);
  try {
    return await fetch(url, {
      ...options,
      signal: timeout.signal,
    });
  } finally {
    timeout.clear();
  }
}

async function readPayload(response) {
  const contentType = response.headers?.get?.('content-type') || '';
  if (/application\/json/i.test(contentType)) {
    const data = typeof response.json === 'function' ? await response.json().catch(() => null) : null;
    return { data, text: data == null ? '' : JSON.stringify(data), isJson: data != null };
  }

  if (typeof response.text !== 'function') {
    const data = typeof response.json === 'function' ? await response.json().catch(() => null) : null;
    return { data, text: data == null ? '' : JSON.stringify(data), isJson: data != null };
  }

  const text = await response.text().catch(() => '');
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { data: JSON.parse(trimmed), text, isJson: true };
    } catch { /* use text below */ }
  }
  return { data: null, text, isJson: false };
}

function responseHeader(response, name) {
  return response.headers?.get?.(name) || '';
}

function isOssResponse(response, payload) {
  const text = payload?.text || '';
  return /AliyunOSS/i.test(responseHeader(response, 'server'))
    || responseHeader(response, 'x-oss-request-id')
    || responseHeader(response, 'x-oss-ec')
    || /<Code>MethodNotAllowed<\/Code>|<Error>/i.test(text);
}

function isStaticHostResponse(response, payload) {
  const text = payload?.text || '';
  const contentType = responseHeader(response, 'content-type');
  return !payload?.isJson
    && (isOssResponse(response, payload)
      || /text\/html|application\/xml|text\/xml/i.test(contentType)
      || /MethodNotAllowed|<!doctype|<html|<Error>/i.test(text));
}

function shouldTryNextBase(response, payload, expectsJson = true) {
  if (isStaticHostResponse(response, payload)) return true;
  const text = payload?.text || '';
  const contentType = responseHeader(response, 'content-type');
  if (expectsJson && !payload?.isJson) {
    return response.ok
      || /text\/html|application\/xml|text\/xml/i.test(contentType)
      || /<!doctype|<html|<Error>/i.test(text);
  }
  if (!expectsJson && response.ok) {
    return /text\/html|application\/xml|text\/xml/i.test(contentType);
  }
  return false;
}

function isGatewayErrorPayload(data) {
  return Boolean(data && typeof data === 'object' && (data.success === false || data.error === true || data.errorCode));
}

function isCdpHealthOkPayload(data) {
  if (!data || typeof data !== 'object') return false;
  if (data.ok === true) return true;
  if (data.success === true && !data.error) return true;
  return false;
}

function errorFromResponse(response, payload, fallbackMessage) {
  const status = Number(response?.status) || 0;
  let transportMessage = '';
  if (status === 413) {
    transportMessage = '上传文件超过网关请求体上限（HTTP 413），请联系运维调整上传限制';
  } else if (status === 408 || status === 504) {
    transportMessage = `PDF 上传或分层处理超过网关等待时间（HTTP ${status}），请稍后重试或联系运维调整超时时间`;
  } else if (status === 502 || status === 503) {
    transportMessage = `Python CDP 分层服务暂时不可用（HTTP ${status}），请检查线上服务进程`;
  } else if (status === 429) {
    transportMessage = 'PDF 分层请求过于频繁（HTTP 429），请稍后重试';
  }
  const message = payload?.data?.error
    || payload?.data?.message
    || payload?.data?.errorDesc
    || payload?.data?.msg
    || transportMessage
    || (isStaticHostResponse(response, payload)
      ? '当前页面是静态站点，/feifan-xxl-art-py/api 没有转发到 CDP 分层服务'
      : `${fallbackMessage}（${response.status}）`);
  const error = new Error(message);
  error.status = response.status;
  error.staticHostResponse = isStaticHostResponse(response, payload);
  return error;
}

function formatCdpError(data) {
  const detail = data?.errorDesc || data?.msg || data?.message;
  if (detail) return detail;
  if (data?.errorCode) return `CDP 分层服务异常（${data.errorCode}）`;
  return 'CDP 服务请求失败';
}

function unwrapCdpPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.success === false || data.error === true) {
    throw new Error(formatCdpError(data));
  }
  if (data.success === true && 'result' in data) return data.result;
  return data;
}

function canRetryOnAnotherBase(response, payload, expectsJson = true) {
  const status = Number(response?.status) || 0;
  if (status === 408 || status === 413 || status === 429 || status >= 500) return false;
  return shouldTryNextBase(response, payload, expectsJson);
}

async function probeBase(base, timeoutMs = DEFAULT_PROBE_TIMEOUT_MS) {
  try {
    const health = await fetchWithTimeout(cdpEngineUrl(CDP_ENDPOINTS.health, base), {
      method: 'GET',
      cache: 'no-store',
    }, timeoutMs);
    const payload = await readPayload(health);
    if (health.ok && isCdpHealthOkPayload(payload?.data)) return true;
    if (payload?.isJson && isGatewayErrorPayload(payload.data)) return false;
    if (isStaticHostResponse(health, payload)) return false;
  } catch { /* older service may not expose health */ }

  try {
    const options = await fetchWithTimeout(cdpEngineUrl(CDP_ENDPOINTS.importPdfLayers, base), {
      method: 'OPTIONS',
      cache: 'no-store',
    }, timeoutMs);
    const payload = await readPayload(options);
    if (isStaticHostResponse(options, payload)) return false;
    if (payload?.isJson && isGatewayErrorPayload(payload.data)) return false;
    return options.status === 204 || options.ok;
  } catch {
    return false;
  }
}

export async function resolveCdpEngineBase(options = {}) {
  if (!options.force && resolvedBase !== undefined) return resolvedBase;
  if (!options.force && resolvingPromise) return resolvingPromise;

  resolvingPromise = (async () => {
    const candidates = getCdpEngineCandidates(options.candidates);
    for (const base of candidates) {
      if (await probeBase(base, options.timeoutMs || DEFAULT_PROBE_TIMEOUT_MS)) {
        resolvedBase = base;
        return resolvedBase;
      }
    }
    resolvedBase = null;
    return null;
  })();

  try {
    return await resolvingPromise;
  } finally {
    resolvingPromise = null;
  }
}

export async function fetchCdpEngineJson(path, requestOptions = {}, options = {}) {
  const fallbackMessage = options.fallbackMessage || 'CDP 服务请求失败';
  const preferredBase = await resolveCdpEngineBase({
    candidates: options.candidates,
    timeoutMs: options.probeTimeoutMs,
  });
  if (preferredBase == null) {
    throw new Error('未找到可用的 Python CDP 分层服务，请确认服务已启动或已配置可用的 VITE_CDP_ENGINE_URL');
  }
  const candidates = getCdpEngineCandidates(options.candidates);
  const ordered = [preferredBase, ...candidates.filter(base => base !== preferredBase)];
  let lastError = null;

  for (const base of ordered) {
    try {
      const response = await fetchWithTimeout(cdpEngineUrl(path, base), requestOptions, options.timeoutMs);
      const payload = await readPayload(response);
      if (response.ok && payload.isJson) {
        resolvedBase = base;
        return unwrapCdpPayload(payload.data);
      }
      const error = errorFromResponse(response, payload, fallbackMessage);
      lastError = error;
      if (canRetryOnAnotherBase(response, payload, true)) {
        if (resolvedBase === base) resolvedBase = undefined;
        continue;
      }
      throw error;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error(options.timeoutMessage || 'CDP 服务请求超时，请稍后重试');
      }
      lastError = error;
      if (error instanceof TypeError) continue;
      throw error;
    }
  }

  throw lastError || new Error('CDP 分层服务不可用，请确认服务已启动或网关可访问');
}

export async function fetchCdpEngineBlob(path, requestOptions = {}, options = {}) {
  const fallbackMessage = options.fallbackMessage || 'CDP 服务请求失败';
  const preferredBase = await resolveCdpEngineBase({
    candidates: options.candidates,
    timeoutMs: options.probeTimeoutMs,
  });
  if (preferredBase == null) {
    throw new Error('未找到可用的 Python CDP 导出服务，请确认服务已启动或已配置可用的 VITE_CDP_ENGINE_URL');
  }
  const candidates = [preferredBase];
  let lastError = null;

  for (const base of candidates) {
    try {
      const response = await fetchWithTimeout(cdpEngineUrl(path, base), requestOptions, options.timeoutMs);
      if (response.ok) {
        if (canRetryOnAnotherBase(response, { isJson: false }, false)) {
          if (resolvedBase === base) resolvedBase = undefined;
          lastError = new Error('当前页面 /feifan-xxl-art-py/api 没有返回 PDF 导出内容');
          continue;
        }
        resolvedBase = base;
        return response.blob();
      }
      const payload = await readPayload(response);
      const error = errorFromResponse(response, payload, fallbackMessage);
      lastError = error;
      if (canRetryOnAnotherBase(response, payload, false)) {
        if (resolvedBase === base) resolvedBase = undefined;
        continue;
      }
      throw error;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error(options.timeoutMessage || 'CDP 服务请求超时，请稍后重试');
      }
      lastError = error;
      if (error instanceof TypeError) continue;
      throw error;
    }
  }

  throw lastError || new Error('CDP 分层服务不可用，请确认服务已启动或网关可访问');
}

export function resetCdpEngineClientForTests() {
  if (!IS_VITEST) return;
  resolvedBase = undefined;
  resolvingPromise = null;
}
